import React from "react";
import {
  Check,
  Info,
  Loader2,
  Pencil,
  Plus,
  RefreshCw,
  X,
} from "lucide-react";
import KpiRelationsLine from "../KpiRelationsLine/KpiRelationsLine";
import { Button } from "@/components/Button/Button";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { AccountMappingState } from "../../hooks/useAccountMappingSection";
import { useAccountPickerDrawer } from "../../hooks/useAccountPickerDrawer";
import { AccountPickerDrawer } from "../AccountPickerDrawer/AccountPickerDrawer";
import { InactiveBadge } from "../InactiveBadge/InactiveBadge";
import {
  LEDGER_ROW_CLASSES,
  LEDGER_TAB_BADGE_CLASSES,
  LEDGER_VIEW_TABS,
  LedgerViewTab,
} from "../../config/accountMappingDisplay";

interface AccountMappingSectionProps {
  /**
   * Passed in rather than called here: the Auto-map button sits in the step
   * header, outside this component, and the two have to read one call of the
   * hook. The caller owns it.
   */
  accountMapping: AccountMappingState;
}

export const AccountMappingSection: React.FC<AccountMappingSectionProps> = ({
  accountMapping,
}) => {
  const {
    accounts,
    ledgerAccounts,
    rows,
    visibleSections,
    tabCounts,
    syncedAt,
    isLoading,
    isSaving,
    savingAccountIds,
    activeTab,
    setActiveTab,
    assignAccounts,
    unassignAccount,
    confirmAccounts,
  } = accountMapping;

  const drawer = useAccountPickerDrawer({
    accounts,
    ledgerAccounts,
    rows,
    assignAccounts,
  });

  // A skeleton rather than a spinner: the section's shape is distinctive
  // enough that showing it settles the layout before the data lands, and
  // nothing here claims a count it does not yet have.
  if (isLoading) {
    return (
      <div className="animate-pulse motion-reduce:animate-none" role="status">
        <span className="sr-only">Loading your chart of accounts…</span>
        <div className="h-11 w-80 rounded-lg bg-gray-100 dark:bg-slate-700" />
        <div className="mt-3 h-10 w-full rounded-md bg-gray-100 dark:bg-slate-700" />
        <div className="mt-3 h-24 w-full rounded-lg bg-gray-100 dark:bg-slate-700" />
        <div className="mt-4 h-3 w-40 rounded bg-gray-100 dark:bg-slate-700" />
        <div className="mt-2 space-y-2">
          <div className="h-20 w-full rounded-lg bg-gray-100 dark:bg-slate-700" />
          <div className="h-20 w-full rounded-lg bg-gray-100 dark:bg-slate-700" />
        </div>
      </div>
    );
  }

  return (
    <div>
      {tabCounts.review > 0 && (
        <div className="overflow-hidden rounded-lg border border-amber-200 bg-amber-50 dark:border-amber-900 dark:bg-amber-950/40">
          <div className="flex items-center gap-3 px-4 py-2.5">
            <span className="flex-1 text-sm text-amber-800 dark:text-amber-200">
              <span className="font-semibold tabular-nums">
                {tabCounts.review}
              </span>{" "}
              {tabCounts.review === 1 ? "account is" : "accounts are"} waiting
              for your review.
            </span>

            <Button
              className="shrink-0 rounded-md"
              onClick={() => setActiveTab("review")}
              size="xs"
              variant="outline"
            >
              <Check />
              Review
            </Button>
          </div>

          <div className="h-1 w-full bg-amber-500" />
        </div>
      )}

      <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
        <ToggleGroup
          className="w-full gap-1 rounded-lg border border-gray-200 bg-white p-1 dark:border-slate-700 dark:bg-slate-800"
          onValueChange={(value) => {
            // Radix clears the value when the selected item is clicked again,
            // and a filter row with nothing selected has no meaning here.
            if (value) {
              setActiveTab(value as LedgerViewTab);
            }
          }}
          type="single"
          value={activeTab}
        >
          {LEDGER_VIEW_TABS.map((tab) => (
            <ToggleGroupItem
              className="group h-auto flex-1 justify-center gap-1.5 px-3 py-1.5 font-normal text-gray-500 hover:bg-transparent hover:text-gray-900 data-[state=on]:bg-primary data-[state=on]:text-white dark:text-gray-400 dark:hover:text-white"
              key={tab.id}
              value={tab.id}
            >
              {tab.label}
              <span
                className={`inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded-full px-1.5 text-xs font-semibold tabular-nums group-data-[state=on]:bg-white group-data-[state=on]:text-gray-900 dark:group-data-[state=on]:bg-white dark:group-data-[state=on]:text-gray-900 ${LEDGER_TAB_BADGE_CLASSES[tab.id]}`}
              >
                {tabCounts[tab.id]}
              </span>
            </ToggleGroupItem>
          ))}
        </ToggleGroup>
      </div>

      <div className="mt-4 space-y-5">
        {visibleSections.map((section) => (
          <div key={section.typeLabel}>
            {/* Uppercase and letter-spaced to match the approved v4.1 design.
                `frontend/CLAUDE.md` otherwise rules all-caps headings out —
                this is the one place the design was signed off against. */}
            <h4 className="px-1 pb-2 text-[11px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
              {section.typeLabel}
            </h4>

            {section.warnings.map((warning) => (
              <div
                className="mb-2 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-200"
                key={warning}
              >
                <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                <span>{warning}</span>
              </div>
            ))}

            <div className="overflow-hidden rounded-lg border border-gray-200 bg-white dark:border-slate-700 dark:bg-slate-800">
              {section.rows.map((row) => {
                const isRowAccepting = row.pending.some((account) =>
                  savingAccountIds.has(account.qboAccountId),
                );

                return (
                  <div
                    className={`border-b border-gray-100 last:border-b-0 dark:border-slate-700/50 ${LEDGER_ROW_CLASSES[row.state]}`}
                    key={row.ledgerAccount.code}
                  >
                    <div className="px-5 py-4">
                      <div
                        className={`-mx-5 flex flex-wrap items-start justify-between gap-4 px-5 ${
                          row.accounts.length > 0
                            ? "border-b border-gray-100 pb-3 dark:border-slate-700/50"
                            : ""
                        }`}
                      >
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="text-[15px] font-medium text-gray-900 dark:text-white">
                              {row.ledgerAccount.label}
                            </span>
                            {row.state === "gap" && (
                              <span className="text-xs text-rose-700 dark:text-rose-300">
                                Nothing mapped here yet
                              </span>
                            )}
                          </div>
                          {row.description !== undefined && (
                            <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                              {row.description}
                            </div>
                          )}
                          <KpiRelationsLine relations={row.kpiRelations} />
                          {/* The state that used to be silent. `empty` means
                              nothing is mapped and no KPI names the row, which
                              the page rendered exactly like a row nothing reads
                              — so a member concluded it was optional while Net
                              Profit % quietly read high. Amber rather than the
                              rose a gap carries: no card is blank, one is just
                              computed without this. */}
                          {row.state === "empty" &&
                            row.summedByKpiNames.length > 0 && (
                              <div className="mt-2 text-xs text-amber-700 dark:text-amber-300">
                                {`Nothing mapped here, so ${row.summedByKpiNames[0]} is computed without it.`}
                              </div>
                            )}
                          {/* Sits under the KPI line on purpose: that line has
                              just said what reads this row, which is what makes
                              "money missing from it" mean something. A banner at
                              the top of the page could name the account but not
                              what it costs. */}
                        </div>

                        <div className="flex shrink-0 items-center gap-3">
                          {/* `[&_svg]:size-3.5` trims the icon the base cva pins
                            at 16px, which overpowers the 12px label at this
                            size. */}

                          {row.pending.length > 0 && (
                            <Button
                              className="rounded-md [&_svg]:size-3.5"
                              disabled={isRowAccepting}
                              onClick={() =>
                                confirmAccounts(
                                  row.pending.map(
                                    (account) => account.qboAccountId,
                                  ),
                                )
                              }
                              size="xs"
                              type="button"
                              variant="outline"
                            >
                              {isRowAccepting ? (
                                <>
                                  <Loader2
                                    className="animate-spin"
                                    strokeWidth={1.75}
                                  />
                                  Accepting…
                                </>
                              ) : (
                                <>
                                  <Check />
                                  Accept All
                                </>
                              )}
                            </Button>
                          )}

                          <Button
                            className="rounded-md [&_svg]:size-3.5"
                            disabled={isSaving}
                            onClick={() => drawer.open(row.ledgerAccount.code)}
                            size="xs"
                            variant="outline"
                          >
                            {row.accounts.length > 0 ? <Pencil /> : <Plus />}
                            {row.accounts.length > 0
                              ? "Edit accounts"
                              : "Choose accounts"}
                          </Button>
                        </div>
                      </div>

                      {/* One account per line: name, code, and the QuickBooks
                        type it came from. */}
                      {row.accounts.length > 0 && (
                        <div className="mt-3 space-y-2">
                          {row.accounts.map((account) => {
                            const isPending =
                              account.mapping?.isConfirmed === false;
                            const isAccountSaving = savingAccountIds.has(
                              account.qboAccountId,
                            );

                            return (
                              <div
                                className="overflow-hidden rounded-md border border-gray-200 dark:border-slate-700"
                                key={account.qboAccountId}
                              >
                                <div
                                  className={`flex items-center justify-between gap-3 px-3 py-2 ${
                                    isPending
                                      ? "bg-amber-50/60 dark:bg-amber-950/20"
                                      : ""
                                  }`}
                                >
                                  <span className="flex min-w-0 items-center gap-2.5 truncate text-xs">
                                    <span className="truncate">
                                      <span className="text-gray-900 dark:text-white">
                                        {account.name}
                                      </span>
                                      {account.accountNumber && (
                                        <span className="text-gray-400 dark:text-gray-500">
                                          {" · "}
                                          {account.accountNumber}
                                        </span>
                                      )}
                                      <span className="ml-2 text-gray-400 dark:text-gray-500">
                                        {account.accountType ?? "—"} →{" "}
                                        {account.accountSubType ?? "—"}
                                      </span>
                                    </span>
                                    {/* Unconditional: this account feeds the
                                        total above, so the member has to be able
                                        to see why a retired one is counted. */}
                                    {!account.isActive && <InactiveBadge />}
                                  </span>

                                  <span className="flex shrink-0 items-center gap-1">
                                    {isAccountSaving ? (
                                      <span
                                        className="inline-flex h-5 w-5 items-center justify-center text-gray-500 dark:text-gray-400"
                                        title="Saving…"
                                      >
                                        <Loader2
                                          className="h-3.5 w-3.5 animate-spin"
                                          strokeWidth={1.75}
                                        />
                                      </span>
                                    ) : (
                                      <>
                                        {isPending && (
                                          <button
                                            className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-amber-200 text-amber-900 transition-colors hover:bg-emerald-500 hover:text-white dark:bg-amber-900 dark:text-amber-100 dark:hover:bg-emerald-600 dark:hover:text-white"
                                            onClick={() =>
                                              confirmAccounts([
                                                account.qboAccountId,
                                              ])
                                            }
                                            title="Accept this mapping"
                                            type="button"
                                          >
                                            <Check className="h-3 w-3" />
                                          </button>
                                        )}
                                        <button
                                          className="inline-flex h-5 w-5 items-center justify-center rounded-full border border-gray-300 text-gray-400 transition-colors hover:border-rose-300 hover:bg-rose-100 hover:text-rose-700 dark:border-slate-600 dark:text-gray-500 dark:hover:border-rose-900 dark:hover:bg-rose-950 dark:hover:text-rose-200"
                                          onClick={() =>
                                            unassignAccount(
                                              account.qboAccountId,
                                            )
                                          }
                                          title="Remove from this account"
                                          type="button"
                                        >
                                          <X className="h-3 w-3" />
                                        </button>
                                      </>
                                    )}
                                  </span>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}

        {visibleSections.length === 0 && (
          <div className="rounded-lg border border-gray-200 bg-white py-10 text-center text-sm text-gray-500 dark:border-slate-700 dark:bg-slate-800 dark:text-gray-400">
            Nothing matches this view.
          </div>
        )}
      </div>

      {syncedAt && (
        <div className="mt-3 flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400">
          <RefreshCw className="h-3.5 w-3.5" />
          Chart of accounts synced {new Date(syncedAt).toLocaleDateString()}
        </div>
      )}

      <AccountPickerDrawer
        accountsInTarget={drawer.accountsInTarget}
        candidates={drawer.candidates}
        nextNeedy={drawer.nextNeedy}
        onClose={drawer.close}
        onCommit={drawer.commit}
        onConfirm={(qboAccountId) => confirmAccounts([qboAccountId])}
        onSelectAllInTier={drawer.selectAllInTier}
        onSwitchTarget={drawer.switchTarget}
        onToggleSelected={drawer.toggleSelected}
        onUnassign={unassignAccount}
        query={drawer.query}
        rows={rows}
        savingAccountIds={savingAccountIds}
        selectedCount={drawer.selectedCount}
        selectedIds={drawer.selectedIds}
        setQuery={drawer.setQuery}
        target={drawer.target}
      />
    </div>
  );
};
